// Leads frios (prospecção): listas, colunas personalizadas, linhas, importação
// e "Mover para o funil". Regras em docs/specs/flows/prospeccao.md. Escrita
// em lote, merge de colunas e promoção passam pelas mesmas RPCs da API/MCP
// (import_prospects, patch_prospect_data, append_prospect_field_options,
// promote_prospect, delete_prospect_field) — transacionais no banco.
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import type { Json, Tables } from "@/integrations/supabase/types";
import { ilikeAnyFilter } from "@/lib/searchFilter";
import { chunk } from "@/lib/sheetImport";
import {
  applyColumnPlan,
  importLimitErrors,
  MAX_IMPORT_ROWS,
  type ColumnPlan,
  type ProspectFieldDef,
  type ProspectFieldType,
  type ProspectStatus,
  type SelectOption,
  slugifyKey,
  uniqueKey,
} from "@/lib/prospectFields";

export type ProspectListRow = Tables<"prospect_lists">;
export interface ProspectList extends ProspectListRow {
  total: number;
  promoted: number;
}

export interface ProspectField extends ProspectFieldDef {
  id: string;
  list_id: string;
  position: number;
  width: number | null;
  hidden: boolean;
}

export type Prospect = Omit<Tables<"prospects">, "data" | "status"> & {
  status: ProspectStatus;
  data: Record<string, unknown>;
};

/** Campos básicos editáveis (o resto vai para `data`). */
export type ProspectCorePatch = Partial<
  Pick<
    Prospect,
    | "name"
    | "contact_name"
    | "email"
    | "phone"
    | "website"
    | "instagram"
    | "city"
    | "state"
    | "notes"
    | "tags"
    | "status"
  >
>;

export interface ProspectFilters {
  listId: string | null;
  status?: ProspectStatus | null;
  search?: string;
  page: number;
  pageSize: number;
}

const LISTS_KEY = ["prospect-lists"] as const;
const SEARCH_COLUMNS = ["name", "contact_name", "city", "phone", "email"];

function toField(row: Tables<"prospect_fields">): ProspectField {
  return {
    ...row,
    type: row.type as ProspectFieldType,
    options: Array.isArray(row.options) ? (row.options as unknown as SelectOption[]) : [],
  };
}

function toProspect(row: Tables<"prospects">): Prospect {
  return {
    ...row,
    status: row.status as ProspectStatus,
    data:
      row.data && typeof row.data === "object" && !Array.isArray(row.data)
        ? (row.data as Record<string, unknown>)
        : {},
  };
}

// ------------------------------------------------------------------ listas

export function useProspectLists() {
  return useQuery({
    queryKey: LISTS_KEY,
    queryFn: async (): Promise<ProspectList[]> => {
      const [lists, summary] = await Promise.all([
        supabase
          .from("prospect_lists")
          .select("*")
          .is("archived_at", null)
          .order("created_at", { ascending: false }),
        supabase.from("prospect_list_summary").select("list_id, total, promoted"),
      ]);
      if (lists.error) throw lists.error;
      if (summary.error) throw summary.error;
      const byId = new Map((summary.data ?? []).map((s) => [s.list_id, s]));
      return (lists.data ?? []).map((l) => ({
        ...l,
        total: byId.get(l.id)?.total ?? 0,
        promoted: byId.get(l.id)?.promoted ?? 0,
      }));
    },
  });
}

export function useCreateProspectList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; description?: string; source?: string }) => {
      const { data, error } = await supabase
        .from("prospect_lists")
        .insert({
          name: input.name.trim(),
          description: input.description?.trim() || null,
          source: input.source ?? "manual",
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: LISTS_KEY }),
    onError: (e: { code?: string }) =>
      toast.error(e.code === "23505" ? "Já existe uma lista com esse nome" : "Erro ao criar lista"),
  });
}

export function useUpdateProspectList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      name,
      archived,
    }: {
      id: string;
      name?: string;
      archived?: boolean;
    }) => {
      const patch: Partial<ProspectListRow> = {};
      if (name !== undefined) patch.name = name.trim();
      if (archived !== undefined) patch.archived_at = archived ? new Date().toISOString() : null;
      const { error } = await supabase.from("prospect_lists").update(patch).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: LISTS_KEY }),
    onError: () => toast.error("Erro ao atualizar lista"),
  });
}

export function useDeleteProspectList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("prospect_lists").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LISTS_KEY });
      qc.invalidateQueries({ queryKey: ["prospects"] });
      toast.success("Lista excluída");
    },
    onError: () => toast.error("Erro ao excluir lista"),
  });
}

// ------------------------------------------------------------------ colunas

export function useProspectFields(listId: string | null) {
  return useQuery({
    queryKey: ["prospect-fields", listId],
    enabled: !!listId,
    queryFn: async (): Promise<ProspectField[]> => {
      const { data, error } = await supabase
        .from("prospect_fields")
        .select("*")
        .eq("list_id", listId!)
        .order("position", { ascending: true });
      if (error) throw error;
      return (data ?? []).map(toField);
    },
  });
}

export function useCreateProspectField() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      listId: string;
      existing: ProspectField[];
      label: string;
      type: ProspectFieldType;
      options?: SelectOption[];
    }) => {
      const key = uniqueKey(slugifyKey(input.label), new Set(input.existing.map((f) => f.key)));
      const position = input.existing.reduce((max, f) => Math.max(max, f.position), 0) + 1;
      const { data, error } = await supabase
        .from("prospect_fields")
        .insert({
          list_id: input.listId,
          key,
          label: input.label.trim(),
          type: input.type,
          options: (input.options ?? []) as unknown as Json,
          position,
        })
        .select()
        .single();
      if (error) throw error;
      return toField(data);
    },
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ["prospect-fields", v.listId] }),
    onError: () => toast.error("Erro ao criar coluna (limite de 150 por lista?)"),
  });
}

export function useUpdateProspectField() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      patch,
    }: {
      id: string;
      listId: string;
      patch: Partial<
        Pick<ProspectField, "label" | "type" | "options" | "hidden" | "width" | "position">
      >;
    }) => {
      const { options, ...rest } = patch;
      const { error } = await supabase
        .from("prospect_fields")
        .update({ ...rest, ...(options ? { options: options as unknown as Json } : {}) })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ["prospect-fields", v.listId] }),
    onError: () => toast.error("Erro ao salvar coluna"),
  });
}

export function useDeleteProspectField() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }: { id: string; listId: string }) => {
      const { error } = await supabase.rpc("delete_prospect_field", { p_id: id });
      if (error) throw error;
    },
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ["prospect-fields", v.listId] });
      qc.invalidateQueries({ queryKey: ["prospects", v.listId] });
      toast.success("Coluna excluída");
    },
    onError: () => toast.error("Erro ao excluir coluna"),
  });
}

// ------------------------------------------------------------------ linhas

export function useProspects(filters: ProspectFilters) {
  return useQuery({
    queryKey: ["prospects", filters.listId, filters],
    enabled: !!filters.listId,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<{ rows: Prospect[]; total: number }> => {
      const from = (filters.page - 1) * filters.pageSize;
      let query = supabase
        .from("prospects")
        .select("*", { count: "exact" })
        .eq("list_id", filters.listId!);
      if (filters.status) query = query.eq("status", filters.status);
      const search = filters.search?.trim();
      if (search) query = query.or(ilikeAnyFilter(SEARCH_COLUMNS, search));
      const { data, error, count } = await query
        .order("created_at", { ascending: false })
        .range(from, from + filters.pageSize - 1);
      if (error) throw error;
      return { rows: (data ?? []).map(toProspect), total: count ?? 0 };
    },
  });
}

function invalidateProspects(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["prospects"] });
  qc.invalidateQueries({ queryKey: LISTS_KEY });
}

export function useCreateProspect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { listId: string; core: ProspectCorePatch & { name: string } }) => {
      const { data, error } = await supabase
        .from("prospects")
        .insert({ ...input.core, list_id: input.listId })
        .select()
        .single();
      if (error) throw error;
      return toProspect(data);
    },
    onSuccess: () => invalidateProspects(qc),
    onError: () => toast.error("Erro ao criar lead frio"),
  });
}

/**
 * Edita uma linha: campos básicos por UPDATE e colunas personalizadas pela
 * RPC `patch_prospect_data` (merge no banco; `null` apaga o valor). O
 * chamador valida `data` com `validateProspectData` antes.
 */
export function useUpdateProspect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      core,
      data,
    }: {
      id: string;
      core?: ProspectCorePatch;
      data?: Record<string, unknown>;
    }) => {
      if (core && Object.keys(core).length) {
        const { error } = await supabase.from("prospects").update(core).eq("id", id);
        if (error) throw error;
      }
      if (data && Object.keys(data).length) {
        const { error } = await supabase.rpc("patch_prospect_data", {
          p_id: id,
          p_patch: data as Json,
        });
        if (error) throw error;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["prospects"] }),
    onError: () => toast.error("Erro ao salvar"),
  });
}

export function useBulkProspectStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ ids, status }: { ids: string[]; status: ProspectStatus }) => {
      const { error } = await supabase
        .from("prospects")
        .update({ status })
        .in("id", ids)
        .neq("status", "promoted");
      if (error) throw error;
    },
    onSuccess: () => invalidateProspects(qc),
    onError: () => toast.error("Erro ao mudar status"),
  });
}

export function useDeleteProspects() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const { error } = await supabase.from("prospects").delete().in("id", ids);
      if (error) throw error;
    },
    onSuccess: (_d, ids) => {
      invalidateProspects(qc);
      toast.success(
        ids.length === 1 ? "Lead frio excluído" : `${ids.length} leads frios excluídos`,
      );
    },
    onError: () => toast.error("Erro ao excluir"),
  });
}

// ------------------------------------------------------------------ importação

export interface ImportProspectsInput {
  listId: string;
  fields: ProspectField[];
  headers: string[];
  records: Record<string, unknown>[];
  plan: ColumnPlan[];
  updateExisting: boolean;
}

export interface ImportProspectsResult {
  inserted: number;
  updated: number;
  skipped: number;
  createdFields: string[];
  errors: { row: number; details: string[] }[];
}

export function useImportProspects() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ImportProspectsInput): Promise<ImportProspectsResult> => {
      const limits = importLimitErrors(input.headers.length, input.plan, input.fields.length);
      if (limits.length) throw new Error(limits.join("; "));

      const result = applyColumnPlan(input.records, input.plan, input.fields);
      let position = input.fields.reduce((max, f) => Math.max(max, f.position), 0);

      if (result.newFields.length) {
        const { error } = await supabase.from("prospect_fields").insert(
          result.newFields.map((f) => ({
            list_id: input.listId,
            key: f.key,
            label: f.label,
            type: f.type,
            options: f.options as unknown as Json,
            position: ++position,
          })),
        );
        if (error) throw error;
      }
      for (const [key, added] of Object.entries(result.optionAdditions)) {
        const field = input.fields.find((f) => f.key === key);
        if (!field) continue;
        const { error } = await supabase.rpc("append_prospect_field_options", {
          p_id: field.id,
          p_options: added as unknown as Json,
        });
        if (error) throw error;
      }

      const totals = { inserted: 0, updated: 0, skipped: 0 };
      for (const batch of chunk(
        result.rows.map((r) => r.row),
        MAX_IMPORT_ROWS,
      )) {
        const { data, error } = await supabase.rpc("import_prospects", {
          p_list_id: input.listId,
          p_rows: batch as unknown as Json,
          p_update_existing: input.updateExisting,
        });
        if (error) throw error;
        const counts = data?.[0];
        totals.inserted += counts?.inserted ?? 0;
        totals.updated += counts?.updated ?? 0;
        totals.skipped += counts?.skipped ?? 0;
      }
      return {
        ...totals,
        createdFields: result.newFields.map((f) => f.label),
        errors: result.errors,
      };
    },
    onSuccess: (_d, v) => {
      invalidateProspects(qc);
      qc.invalidateQueries({ queryKey: ["prospect-fields", v.listId] });
    },
  });
}

// ------------------------------------------------------------------ funil

export interface PromoteOverrides {
  name?: string;
  email?: string | null;
  phone?: string | null;
  message?: string;
  project_type?: string;
}

/** Resolve com o id do lead criado (ou o já existente — idempotente). */
export function usePromoteProspect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, overrides }: { id: string; overrides?: PromoteOverrides }) => {
      const { data, error } = await supabase.rpc("promote_prospect", {
        p_id: id,
        p_overrides: (overrides ?? {}) as Json,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      invalidateProspects(qc);
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["admin-inbox"] });
    },
    onError: (e: { code?: string }) =>
      toast.error(e.code === "22023" ? "E-mail inválido" : "Erro ao mover para o funil"),
  });
}

/** Lead frio de origem de um lead (card "Origem" no LeadDetail). */
export function useProspectForLead(leadId: string | undefined) {
  return useQuery({
    queryKey: ["prospect-for-lead", leadId],
    enabled: !!leadId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("prospects")
        .select("*, prospect_lists(name)")
        .eq("lead_id", leadId!)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const { data: fields, error: fieldsError } = await supabase
        .from("prospect_fields")
        .select("*")
        .eq("list_id", data.list_id)
        .order("position", { ascending: true });
      if (fieldsError) throw fieldsError;
      const { prospect_lists, ...row } = data as typeof data & {
        prospect_lists: { name: string } | null;
      };
      return {
        prospect: toProspect(row as Tables<"prospects">),
        listName: prospect_lists?.name ?? "",
        fields: (fields ?? []).map(toField),
      };
    },
  });
}
